import { action, computed, observable, makeAutoObservable } from 'mobx';
import { Resource } from 'src_common/common/mobx-utils/Resource';
import { apiCommon } from 'src/api/ApiCommon';
import { timeout } from 'src_common/common/mobx-utils/timeout';
import { MobileUpdateVersionModelType } from 'src/api/config/mobileAppUpdateVersion/getAppUpdateVersion';
import {
    ParamsType,
    Response200Type,
} from 'src/api_openapi/generated/openapi_mobile_app_version_update_createMobileAppUpdateVersion';
import { FormModel, Result } from 'src_common/common/mobx-utils/Form2/FormModel';
import { FormInputState } from 'src_common/common/mobx-utils/Form2/FormInputState';
import { validateNotEmpty } from 'src_common/common/mobx-utils/Form2/validators';

type AppMobileVersionBodyType = ParamsType['body'];

export interface AppMobileVersionType {
    app_version: string;
    force_update: boolean;
    platform: string;
}

export const platformOptions = [
    {
        key: 'ios',
        name: 'iOS',
    },
    {
        key: 'android',
        name: 'Android',
    },
];

const validateAppVersion =
    (message: string) =>
    (value: string): Result<string> => {
        const firstLetter = Number(value[0]);
        const lastLetter = Number(value[value.length - 1]);

        if (isNaN(firstLetter) || isNaN(lastLetter)) {
            return Result.createError(message);
        }

        const valueWithoutDots = Number(value.replaceAll('.', ''));

        if (isNaN(valueWithoutDots)) {
            return Result.createError(message);
        }

        return Result.createOk(value);
    };

export class AppUpdateVersionState {
    @observable private readonly appVersionListResource: Resource<Array<MobileUpdateVersionModelType>>;
    @observable public errorMessageForList: string | null = null;

    public readonly appVersion: FormInputState<string, string>;
    public readonly platform: FormInputState<string, string>;
    public readonly isForcedUpdate: FormInputState<boolean, boolean>;

    private readonly form: FormModel<AppMobileVersionType>;

    constructor() {
        this.appVersionListResource = new Resource(async (): Promise<Array<MobileUpdateVersionModelType>> => {
            return await apiCommon.getMobileUpdateVersion.run({});
        });

        this.appVersion = FormInputState.new('')
            .map(validateNotEmpty('Cannot be not empty'))
            .map(validateAppVersion('Invalid version format - ex. - 1.2.3'));

        this.platform = FormInputState.new('ios');

        this.isForcedUpdate = FormInputState.new<boolean>(false);
        this.form = FormModel.group({
            app_version: this.appVersion,
            platform: this.platform,
            force_update: this.isForcedUpdate,
        });
        makeAutoObservable(this);
    }

    @action private handleCreateApiCall = async (
        appMobileVersion: AppMobileVersionBodyType
    ): Promise<Response200Type | null> => {
        try {
            return await apiCommon.postCreateMobileUpdateVersion.run({ data: appMobileVersion });
        } catch (error) {
            console.error(error);
            return null;
        }
    };

    @action public handleCreate = async (): Promise<void> => {
        this.form.setAsVisited();
        const result = this.form.result;
        const body = result.value;

        if (body.type === 'ok') {
            const submitResult = await this.handleCreateApiCall(body.data);

            if (submitResult !== null) {
                await this.appVersionListResource.refresh();
                this.form.reset();
            } else {
                console.log('Error');
                this.handleError('Something went wrong.');
            }
        }
    };

    @computed public get appMobileVersionList(): Array<MobileUpdateVersionModelType> {
        const appMobileVersionListResource = this.appVersionListResource.get();
        if (appMobileVersionListResource.type === 'ready') {
            return appMobileVersionListResource.value;
        }
        return [];
    }

    @computed public get appMobileVersionSortedList(): Array<MobileUpdateVersionModelType> {
        return this.appMobileVersionList.sort((a, b) => {
            const aVersion = a.app_version;
            const bVersion = b.app_version;

            const aPlatform = a.platform;
            const bPlatform = b.platform;

            return aVersion.localeCompare(bVersion) || aPlatform.localeCompare(bPlatform);
        });
    }

    @computed public get isLoading(): boolean {
        const appMobileVersionListResource = this.appVersionListResource.get();
        return appMobileVersionListResource.type === 'loading';
    }

    @action public handleRemoveApiCall = async (app_version: string, platform: string): Promise<void> => {
        const removeResult = await apiCommon.deleteMobileUpdateVersion.run({ app_version, platform });

        if (removeResult) {
            await this.appVersionListResource.refresh();
        } else {
            console.error('Error while removing app version');
        }
    };

    @action public setActiveFlag = (app_version: string, platform: string): void => {
        const appMobileVersion =
            this.appMobileVersionList.find((elem) => elem.app_version === app_version && elem.platform === platform) ??
            null;
        if (appMobileVersion !== null) {
            const payload: MobileUpdateVersionModelType = {
                ...appMobileVersion,
                force_update: !appMobileVersion.force_update,
            };

            this.handleEditActiveFlags(payload);
        }
    };

    @action private handleEditApiCall = async (
        appMobileVersion: MobileUpdateVersionModelType
    ): Promise<number | null> => {
        try {
            return await apiCommon.updateMobileUpdateVersion.run({ data: appMobileVersion });
        } catch (error) {
            console.error(error);
            return null;
        }
    };

    @action private handleEditActiveFlags = async (payload: MobileUpdateVersionModelType): Promise<void> => {
        const submitResult = await this.handleEditApiCall(payload);

        if (submitResult !== null) {
            await this.appVersionListResource.refresh();
            this.form.reset();
        } else {
            console.log('Error');
        }
    };

    @action handleError = async (message: string): Promise<void> => {
        this.errorMessageForList = message;
        await timeout(3000);
        this.errorMessageForList = null;
    };
}
